import React from "react";
import { FileTextOutlined, SafetyCertificateOutlined } from "@ant-design/icons";
import { formatMetaDate } from "../../utils/internalAuditView";

export const EMPTY_SECTION_PROMPT = "Write this section or generate an AI draft for review";

const TYPE_BADGE = {
  Conformity: "border-emerald-200 bg-emerald-50 text-emerald-800",
  "NC Major": "border-rose-200 bg-rose-50 text-rose-800",
  "NC Minor": "border-amber-200 bg-amber-50 text-amber-800",
  Observation: "border-amber-200 bg-amber-50 text-amber-900",
};

function SectionTitle({ children }) {
  return (
    <h4 className="mb-3 border-b-2 border-blue-500 pb-1 text-sm font-black uppercase tracking-wide text-slate-800">
      {children}
    </h4>
  );
}

function FindingTypeBadge({ type }) {
  const t = type || "-";
  const cls = TYPE_BADGE[t] || "border-slate-200 bg-slate-50 text-slate-700";
  return (
    <span className={`inline-flex rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>
      {t}
    </span>
  );
}

function EmptySection({ name }) {
  return (
    <p data-empty-section={name} className="text-sm font-medium italic text-amber-800">
      {EMPTY_SECTION_PROMPT}
    </p>
  );
}

function FindingsTable({ title, rows, technical }) {
  if (!rows?.length) return null;
  return (
    <div className="mt-8">
      <SectionTitle>{title}</SectionTitle>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full min-w-[640px] border-collapse text-left text-xs">
          <thead>
            <tr className="bg-slate-100/90 text-[10px] font-black uppercase tracking-widest text-slate-600">
              <th className="border-b border-slate-200 px-3 py-2.5">Clause / Control #</th>
              <th className="border-b border-slate-200 px-3 py-2.5">
                {technical ? "Impacted asset(s)" : "Applicable requirements"}
              </th>
              <th className="border-b border-slate-200 px-3 py-2.5">Finding type</th>
              <th className="border-b border-slate-200 px-3 py-2.5">Finding #</th>
              <th className="border-b border-slate-200 px-3 py-2.5">Evidence</th>
              <th className="border-b border-slate-200 px-3 py-2.5">Auditor comments</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.findingId} className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-2.5 font-semibold text-slate-800">{r.clause}</td>
                <td className="px-3 py-2.5 text-slate-600">
                  {technical ? r.impactedAssets : r.requirement}
                </td>
                <td className="px-3 py-2.5">
                  <FindingTypeBadge type={r.findingType} />
                </td>
                <td className="px-3 py-2.5 font-mono text-[11px] text-slate-600">{r.findingId}</td>
                <td className="px-3 py-2.5 text-slate-600">{r.evidence}</td>
                <td className="px-3 py-2.5 text-slate-700">{r.auditorComments}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Formatted internal audit report document (modal / print-style preview).
 */
export function InternalAuditReportDocument({ snapshot, project }) {
  const view = snapshot?.view;
  const meta = snapshot?.metadata || {};
  const summaryText = String(view?.executiveSummaryText || "").trim();
  const opinionText = String(view?.affirmationQuote || "").trim();

  if (!view) {
    return (
      <p className="py-8 text-center text-sm text-slate-500">Report document data is not available.</p>
    );
  }

  const companyDisplay = view.companyName || project?.companyName || "-";

  return (
    <div className="bg-white text-slate-900">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-6 border-b border-slate-100 pb-6">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-md">
            <SafetyCertificateOutlined className="text-xl" />
          </div>
          <div>
            <div className="text-lg font-black text-slate-900">{companyDisplay}</div>
            <div className="text-xs font-medium text-slate-500">Legal Entity</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Lead Auditor</div>
          <span className="mt-1 inline-flex rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-bold text-slate-700">
            {view.signatureName || meta.leadAuditor || "-"}
          </span>
        </div>
      </div>

      <h2 className="mb-8 text-center text-2xl font-black tracking-tight text-slate-900">Internal Audit Report</h2>

      <SectionTitle>Executive summary</SectionTitle>
      <div className="rounded-2xl border border-slate-100 bg-slate-50/80 p-5">
        {summaryText ? (
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{summaryText}</p>
        ) : (
          <EmptySection name="executive-summary" />
        )}
      </div>

      <div className="mt-8">
        <SectionTitle>Scope</SectionTitle>
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full border-collapse text-sm">
            <tbody>
              <tr className="border-b border-slate-100">
                <td className="w-[38%] bg-slate-50/90 px-4 py-3 font-semibold text-slate-600">
                  Legal entities + Locations
                </td>
                <td className="px-4 py-3 text-slate-800">{view.scope.legalEntities}</td>
              </tr>
              <tr className="border-b border-slate-100">
                <td className="bg-slate-50/90 px-4 py-3 font-semibold text-slate-600">Products and services</td>
                <td className="px-4 py-3 text-slate-800">{view.scope.productsServices}</td>
              </tr>
              <tr>
                <td className="bg-slate-50/90 px-4 py-3 font-semibold text-slate-600">
                  Activities / Depts / Functions
                </td>
                <td className="px-4 py-3 text-slate-800">{view.scope.activitiesDepts}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8">
        <SectionTitle>Audit criteria</SectionTitle>
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-blue-50/80 text-left text-[10px] font-black uppercase tracking-widest text-slate-600">
                <th className="border-b border-slate-200 px-4 py-2.5">Title</th>
                <th className="border-b border-slate-200 px-4 py-2.5 text-right">
                  Count of requirements / controls
                </th>
              </tr>
            </thead>
            <tbody>
              {view.auditCriteria.map((row) => (
                <tr key={row.title} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2.5 font-medium text-slate-800">{row.title}</td>
                  <td className="px-4 py-2.5 text-right font-black text-slate-900">{row.controlCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <FindingsTable title="Non-technical assessment" rows={view.nonTechnicalFindings} technical={false} />
      <FindingsTable title="Technical assessment" rows={view.technicalFindings} technical />

      {snapshot.auditReport?.methodology ? (
        <div className="mt-8">
          <SectionTitle>Methodology</SectionTitle>
          <p className="text-sm leading-relaxed text-slate-700">{snapshot.auditReport.methodology}</p>
        </div>
      ) : null}

      {snapshot.auditReport?.conclusions ? (
        <div className="mt-8">
          <SectionTitle>Conclusions</SectionTitle>
          <p className="text-sm leading-relaxed text-slate-700">{snapshot.auditReport.conclusions}</p>
        </div>
      ) : null}

      <div className="relative mt-10 border-l-4 border-blue-600 py-2 pl-5">
        {opinionText ? (
          <p className="text-sm italic leading-relaxed text-slate-700">&ldquo;{opinionText}&rdquo;</p>
        ) : (
          <EmptySection name="auditor-opinion" />
        )}
      </div>

      <div className="mt-10 flex flex-wrap items-end justify-between gap-8 border-t border-slate-100 pt-8">
        <div>
          <div className="text-sm font-bold text-slate-800">{view.signatureName || "-"}</div>
          <div className="mt-6 h-px w-48 bg-slate-300" />
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-600">{formatMetaDate(view.signatureDate)}</div>
          <div className="mt-6 h-px w-32 bg-slate-300" />
        </div>
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-300">&lt;CONFIDENTIAL&gt;</div>
      </div>
    </div>
  );
}

/** Modal chrome title row matching document header. */
export function InternalAuditReportModalTitle({ subtitle }) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-600 text-white">
        <FileTextOutlined />
      </div>
      <div>
        <div className="text-base font-black text-slate-900">Internal Audit Report</div>
        <div className="text-xs font-medium text-slate-500">{subtitle || ""}</div>
      </div>
    </div>
  );
}
